import { useCallback, useRef, useEffect } from 'react';
import { AgentService } from '@/services/agent';
import { useAgentStore, STREAMING_RESET } from '@/stores/agentStore';
import type { Message, ChatHistoryEntry } from '../types';
import {
  summarizeTopic,
  formatSessionDate,
  restoreBackendMessage,
  createProcessLog,
} from '../helpers';
import { useStreamPolling } from './useStreamPolling';

interface SessionSnapshot {
  messages: Message[];
  streamingText: string | null;
  activeProcessLogs: any[];
  activeAssistantIntro: string | null;
  isLoading: boolean;
  isThinking: boolean;
  thinkingSeconds: number;
  pendingBackendMsgIds: { user_msg_id?: string; assistant_msg_id?: string } | null;
  pendingCanvasNodeId: string | null;
  currentIntent: string;
}

export function useChatHistory() {
  const hasHydratedStorageRef = useRef(false);
  const sessionSnapshotsRef = useRef<Map<string, SessionSnapshot>>(new Map());
  const activeSessionIdRef = useRef<string | null>(null);

  const { startPolling, stopAllPolling } = useStreamPolling({
    onComplete: (_content, _msgId) => {
      // 🔴 F-15: resetStreamingState 已在 polling 原子操作中执行（STREAMING_RESET）
      // 此处仅需设置 selectedHistoryId 和刷新历史
      if (activeSessionIdRef.current) {
        useAgentStore.getState().setSelectedHistoryId(activeSessionIdRef.current);
      }
      refreshChatHistory();
    },
    onFailed: (error, _msgId) => {
      const store = useAgentStore.getState();
      store.updateActiveProcessLogs((prev) => {
        const next = [...prev];
        const activeIndex = next.map((log) => log.status).lastIndexOf('active');
        if (activeIndex === -1) return next;
        next[activeIndex] = { ...next[activeIndex], kind: 'error', status: 'error', title: '后台生成失败', detail: error || '未知错误' };
        return next;
      });
      store.resetStreamingState();
    },
  });

  const MAX_HISTORY_ENTRIES = 20;

  const sortAndLimitHistory = (entries: ChatHistoryEntry[]): ChatHistoryEntry[] => {
    return entries
      .sort((a, b) => {
        // 按 updatedAt 降序排列，最新的在前
        const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return tb - ta;
      })
      .slice(0, MAX_HISTORY_ENTRIES);
  };

  const refreshChatHistory = useCallback(async () => {
    const { useProjectStore: _useProjectStore } = await import('@/stores/projectStore');
    const projectId = _useProjectStore.getState().currentProjectId;
    if (!projectId) return;
    try {
      const result = await AgentService.listSessions(projectId);
      if (result.sessions && result.sessions.length > 0) {
        const historyEntries: ChatHistoryEntry[] = result.sessions
          .filter(session => session.messageCount === undefined || session.messageCount > 0)
          .map(session => ({
            id: session.sessionId,
            topic: session.topic ? summarizeTopic(session.topic) : '未命名对话',
            date: formatSessionDate(session.updatedAt),
            updatedAt: session.updatedAt || '',
            messages: [],
          }));
        useAgentStore.getState().setChatHistory(prev => {
          const backendIds = new Set(historyEntries.map(h => h.id));
          // 合并：后端结果优先，保留不在后端结果中的本地记录
          const merged = historyEntries.map(backend => {
            const existing = prev.find(h => h.id === backend.id);
            if (existing && !backend.date) return { ...backend, date: existing.date };
            return backend;
          });
          for (const h of prev) {
            if (!backendIds.has(h.id)) {
              merged.push(h);
            }
          }
          return sortAndLimitHistory(merged);
        });
      }
    } catch (err) {
      console.warn('Failed to refresh chat history:', err);
    }
  }, []);

  const saveCurrentSessionSnapshot = useCallback(() => {
    const sid = activeSessionIdRef.current;
    if (!sid) return;
    const store = useAgentStore.getState();
    sessionSnapshotsRef.current.set(sid, {
      messages: store.messages,
      streamingText: store.streamingText,
      activeProcessLogs: store.activeProcessLogs,
      activeAssistantIntro: store.activeAssistantIntro,
      isLoading: store.isLoading,
      isThinking: store.isThinking,
      thinkingSeconds: store.thinkingSeconds,
      pendingBackendMsgIds: store.pendingBackendMsgIds,
      pendingCanvasNodeId: store.pendingCanvasNodeId,
      currentIntent: store.currentIntent,
    });
  }, []);

  const restoreSessionSnapshot = useCallback((sid: string): boolean => {
    const snapshot = sessionSnapshotsRef.current.get(sid);
    if (!snapshot) return false;
    // 🔴 P3-1: 恢复历史会话时，**完全重置流式状态**，防止残留的 activeReplyBuilder/streamingText
    // 历史消息已经 finalize，不需要保存的流式构建器或文本片段
    useAgentStore.setState({
      messages: snapshot.messages.map((m) => ({ ...m, isHistory: true })),
      streamingText: null,            // P3-1: 强制清除流式文本
      activeProcessLogs: [],           // P3-1: 清空处理日志
      activeAssistantIntro: null,      // P3-1: 清空引言
      isLoading: false,                // P3-1: 重置加载状态
      isThinking: false,               // P3-1: 重置思考状态
      thinkingSeconds: 0,              // P3-1: 重置计时
      pendingBackendMsgIds: null,      // P3-1: 清除待定消息ID
      pendingCanvasNodeId: null,       // P3-1: 清除canvas节点
      activeReplyBuilder: null,        // P3-1: 清除流式构建器（关键！）
      currentIntent: 'chat',           // P3-1: 恢复默认意图
      lastChunkReceivedAt: null,       // P3-1: 清除SSE时间戳
      abortController: null,           // 🔴 F-20: 清除中断控制器，防止残留的 abortController 阻断新请求
      isFinalizing: false,             // 🔴 F-20: 重置 finalize 标志
    });
    sessionSnapshotsRef.current.delete(sid);
    return true;
  }, []);

  const handleNewChat = useCallback(async () => {
    const currentSid = activeSessionIdRef.current;
    const store = useAgentStore.getState();
    if (currentSid && (store.isLoading || store.isThinking || store.streamingText)) {
      saveCurrentSessionSnapshot();
    }
    store.clearThinkingTimer();
    store.resetSession();
    store.setSelectedHistoryId(null);

    // 确保流式状态完全清理（防止 isComplete=0 的残留消息触发恢复逻辑）
    store.setIsLoading(false);
    store.setStreamingText(null);
    store.setActiveProcessLogs([]);
    store.setActiveAssistantIntro(null);

    sessionSnapshotsRef.current.clear();
    const { useProjectStore: _useProjectStore } = await import('@/stores/projectStore');
    const projectId = _useProjectStore.getState().currentProjectId;
    if (projectId) {
      try {
        const result = await AgentService.createSession(projectId);
        useAgentStore.getState().setSessionId(result.sessionId);
        activeSessionIdRef.current = result.sessionId;
      } catch (err) {
        console.warn('Failed to create new session:', err);
      }
    }
    refreshChatHistory();
  }, [saveCurrentSessionSnapshot, refreshChatHistory]);

  const handleIncompleteMessages = useCallback((
    sessionId: string,
    messages: any[],
    restoredMsgs: Message[],
  ) => {
    const incompleteMsg = messages.find(
      (m: any) => m.role === 'assistant' && (m.isComplete === 0 || m.isComplete === 2)
    );

    if (incompleteMsg && incompleteMsg.isComplete === 0) {
      const store = useAgentStore.getState();
      store.setIsLoading(true);
      store.setStreamingText(incompleteMsg.content || '');
      store.setActiveProcessLogs([createProcessLog('action', '恢复后台生成中', 'AI 正在后台继续生成回复', 'active')]);
      startPolling(sessionId, incompleteMsg.id);
    } else if (incompleteMsg && incompleteMsg.isComplete === 2) {
      const msgIdx = restoredMsgs.findIndex((m: Message) => m.id === incompleteMsg.id);
      if (msgIdx >= 0) {
        const updated = [...restoredMsgs];
        updated[msgIdx] = {
          ...updated[msgIdx],
          text: updated[msgIdx].text + '\n\n🔄 生成被中断，点击可重新生成',
          metadata: {
            ...(updated[msgIdx].metadata || {}),
            wasInterrupted: true,
          },
        };
        useAgentStore.getState().setMessages(updated);
      }
    }
  }, [startPolling]);

  const handleLoadHistory = useCallback(async (entry: ChatHistoryEntry) => {
    const currentSid = activeSessionIdRef.current;
    if (currentSid && currentSid !== entry.id) {
      // 🔴 F-23: 切换会话前停止旧会话的所有 polling，避免状态交叉污染
      // 即使旧会话的 SSE 已中断，polling 可能仍在运行
      stopAllPolling();
      saveCurrentSessionSnapshot();
      useAgentStore.getState().clearThinkingTimer();
    }

    refreshChatHistory();
    const store = useAgentStore.getState();
    store.setSelectedHistoryId(entry.id);
    store.setShowHistoryModal(false);

    const restored = restoreSessionSnapshot(entry.id);
    if (restored) {
      store.setSessionId(entry.id);
      activeSessionIdRef.current = entry.id;
      return;
    }

    try {
      const result = await AgentService.getSessionMessages(entry.id);
      if (result.messages && result.messages.length > 0) {
        const restoredMsgs = result.messages.map(restoreBackendMessage).map((m) => ({ ...m, isHistory: true }));
        // 🔴 F-22: 使用 STREAMING_RESET 统一重置流式状态，避免手动逐字段重置遗漏
        const hasIncomplete = result.messages.some((m: any) => m.role === 'assistant' && m.isComplete === 0);
        useAgentStore.setState({
          messages: restoredMsgs,
          ...STREAMING_RESET,
          // 有未完成消息时保留 isLoading（由 handleIncompleteMessages 后续设置）
          ...(hasIncomplete ? {} : {}),
        });
        // 同步更新 chatHistory 中对应条目的 messages
        useAgentStore.getState().setChatHistory(prev =>
          prev.map(h => h.id === entry.id ? { ...h, messages: restoredMsgs } : h)
        );
        handleIncompleteMessages(entry.id, result.messages, restoredMsgs);
      } else {
        // 🔴 F-22: 空消息也使用 STREAMING_RESET
        useAgentStore.setState({
          messages: [],
          ...STREAMING_RESET,
        });
        useAgentStore.getState().setChatHistory(prev =>
          prev.map(h => h.id === entry.id ? { ...h, messages: [] } : h)
        );
      }
      useAgentStore.getState().setSessionId(entry.id);
      activeSessionIdRef.current = entry.id;
    } catch (err) {
      console.warn('Failed to load session from backend:', err);
      useAgentStore.setState({
        messages: [],
        ...STREAMING_RESET,
      });
      useAgentStore.getState().setSessionId(entry.id);
      activeSessionIdRef.current = entry.id;
    }
  }, [saveCurrentSessionSnapshot, restoreSessionSnapshot, refreshChatHistory, handleIncompleteMessages]);

  const handleDeleteHistory = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    useAgentStore.getState().setChatHistory(prev => prev.filter(h => h.id !== id));
    sessionSnapshotsRef.current.delete(id);
    const store = useAgentStore.getState();
    if (store.selectedHistoryId === id) {
      store.clearThinkingTimer();
      store.resetSession();
      store.setSelectedHistoryId(null);
      activeSessionIdRef.current = null;
    }
    try {
      await AgentService.closeSession(id);
    } catch (err) {
      console.warn('Failed to close session on backend:', err);
    }
  }, []);

  const startPollingRef = useRef(startPolling);
  startPollingRef.current = startPolling;

  useEffect(() => {
    let cancelled = false;

    const restoreFromBackend = async (sessionId: string) => {
      try {
        const result = await AgentService.getSessionMessages(sessionId);
        if (cancelled) return false;
        if (result.messages && result.messages.length > 0) {
          const restored = result.messages.map(restoreBackendMessage);
          useAgentStore.getState().setMessages(restored);

          const incompleteMsg = result.messages.find(
            (m: any) => m.role === 'assistant' && m.isComplete === 0
          );
          if (incompleteMsg) {
            const store = useAgentStore.getState();
            store.setIsLoading(true);
            store.setStreamingText(incompleteMsg.content || '');
            store.setActiveProcessLogs([createProcessLog('action', '恢复后台生成中', 'AI 正在后台继续生成回复', 'active')]);
            startPollingRef.current(sessionId, incompleteMsg.id);
          }

          return true;
        }
      } catch (err) {
        console.warn('Failed to restore agent messages from backend:', err);
      }
      return false;
    };

    const restoreSessionsFromBackend = async (_currentSessionId: string | null) => {
      const { useProjectStore: _useProjectStore } = await import('@/stores/projectStore');
      if (cancelled) return;
      const projectId = _useProjectStore.getState().currentProjectId;
      if (!projectId) return;
      try {
        const result = await AgentService.listSessions(projectId);
        if (cancelled) return;
        if (result.sessions && result.sessions.length > 0) {
          const historyEntries: ChatHistoryEntry[] = result.sessions
            .filter(session => session.messageCount === undefined || session.messageCount > 0)
            .map(session => ({
              id: session.sessionId,
              topic: session.topic ? summarizeTopic(session.topic) : '未命名对话',
              date: formatSessionDate(session.updatedAt),
              updatedAt: session.updatedAt || '',
              messages: [],
            }));
          useAgentStore.getState().setChatHistory(sortAndLimitHistory(historyEntries));
        }
      } catch (err) {
        console.warn('Failed to restore sessions from backend:', err);
      }
    };

    const tryRestore = async () => {
      try {
        const agentSessionId = useAgentStore.getState().sessionId;
        if (agentSessionId) {
          useAgentStore.getState().setSelectedHistoryId(agentSessionId);
          activeSessionIdRef.current = agentSessionId;
          const backendRestored = await restoreFromBackend(agentSessionId);
          if (cancelled) return;
          await restoreSessionsFromBackend(agentSessionId);
          if (backendRestored) return;
        } else {
          await restoreSessionsFromBackend(null);
        }
      } catch (error) {
        console.warn('Backend restore failed:', error);
      } finally {
        if (!cancelled) {
          hasHydratedStorageRef.current = true;
        }
      }
    };

    tryRestore();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleOnline = async () => {
      const sessionId = activeSessionIdRef.current;
      if (!sessionId) return;

      try {
        const result = await AgentService.getIncompleteMessages(sessionId);
        if (result.messages && result.messages.length > 0) {
          const runningMsg = result.messages.find((m: any) => m.isComplete === 0);
          if (runningMsg) {
            const store = useAgentStore.getState();
            store.setIsLoading(true);
            store.setStreamingText(runningMsg.content || '');
            store.setActiveProcessLogs([createProcessLog('action', '网络恢复，继续追踪后台生成', 'AI 正在后台继续生成回复', 'active')]);
            startPollingRef.current(sessionId, runningMsg.id);
          }
        }
      } catch (err) {
        console.warn('[ChatHistory] Failed to check incomplete messages on reconnect:', err);
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  return {
    refreshChatHistory,
    handleNewChat,
    handleLoadHistory,
    handleDeleteHistory,
    saveCurrentSessionSnapshot,
    restoreSessionSnapshot,
  };
}
